import useSWR from 'swr';
import { useMemo } from 'react';

import axios, { fetcher, endpoints } from 'src/utils/axios';

import { DEFAULT_SWR_OPTIONS } from 'src/config/swr';

export function useGetAuditTrailEventTypes() {
  const url = endpoints.auditTrails.getEventTypes;

  const { data, isLoading, error, isValidating } = useSWR<string[]>(
    url,
    fetcher,
    DEFAULT_SWR_OPTIONS
  );

  const memoizedValue = useMemo(
    () => ({
      eventTypes: data || [],
      eventTypesLoading: isLoading,
      eventTypesError: error,
      eventTypesValidating: isValidating,
      eventTypesEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetAuditTrailEntityTypes() {
  const url = endpoints.auditTrails.getEntityTypes;

  const { data, isLoading, error, isValidating } = useSWR<string[]>(
    url,
    fetcher,
    DEFAULT_SWR_OPTIONS
  );

  const memoizedValue = useMemo(
    () => ({
      entityTypes: data || [],
      entityTypesLoading: isLoading,
      entityTypesError: error,
      entityTypesValidating: isValidating,
      eventTypesEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

/**
 * Exports audit trails with the given filters and parameters.
 * @param params - Export parameters including filters and pagination
 * @returns Blob data for download
 */
export async function exportAuditTrails(params: {
  page: number;
  take: number;
  search?: string;
  language?: string;
  entityTypes?: string;
  actionTypes?: string;
  entityIds?: string;
  userIds?: string;
  startDate?: string;
  endDate?: string;
}): Promise<Blob> {
  try {
    const timestamp = new Date().getTime();
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });
    
    queryParams.append('_', String(timestamp));
    
    const response = await axios.get(`${endpoints.auditTrails.get}?${queryParams.toString()}`, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv',
        'Cache-Control': 'no-cache, no-store',
        Pragma: 'no-cache',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error exporting audit trails:', error);
    throw error;
  }
}
